import React, { useState, useEffect } from 'react';
import { AiOutlineClose, AiOutlineSave } from 'react-icons/ai';
import { 
  HiOutlineInformationCircle,
  HiOutlineSparkles,
//   HiOutlineCog,
} from 'react-icons/hi';
// import { 
//   FiSettings,
//   FiToggleLeft,
//   FiToggleRight,
//   FiCheckCircle,
//   FiXCircle,
//   FiLayers,
// } from 'react-icons/fi';
import { 
  getTenantServiceOptions,  
  upsertTenantServiceOption,
//   deleteTenantServiceOption
} from '../../../shared/services/apis/tenant';
import type { TenantServiceOption, TenantServiceOptionData } from '../../../shared/types/tenant';
import Dialog from '../../../shared/ui/dialog';

interface TenantServiceOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  serviceId: string;
  serviceName: string;
  onOptionsUpdate?: () => void;
}

interface NewOptionData extends TenantServiceOptionData {
  id?: string;
}

const TenantServiceOptionsModal: React.FC<TenantServiceOptionsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  serviceId,
  onOptionsUpdate
}) => {
  const [options, setOptions] = useState<TenantServiceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingOption, setEditingOption] = useState<string | null>(null);
  const [newOption, setNewOption] = useState<NewOptionData>({
    key: '',
    value: '',
    valueType: 'STRING',
    isRequired: false,
    isActive: true,
    description: '',
    category: 'general',
    displayOrder: 0
  });

  useEffect(() => {
    if (isOpen && tenantId && serviceId) {
      fetchOptions();
    }
  }, [isOpen, tenantId, serviceId]);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedOptions = await getTenantServiceOptions(tenantId, serviceId);
      setOptions(fetchedOptions);
    } catch (err) {
      setError('Failed to fetch service options');
      console.error('Error fetching service options:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOption = async () => {
    if (!newOption.key.trim()) {
      setError('Option key is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await upsertTenantServiceOption(tenantId, serviceId, newOption.key, {
        key: newOption.key,
        value: newOption.value || '',
        valueType: newOption.valueType,
        isRequired: newOption.isRequired,
        isActive: newOption.isActive,
        description: newOption.description,
        category: newOption.category,
        displayOrder: newOption.displayOrder
      });
      
      await fetchOptions();
      setShowAddForm(false);
      resetNewOption();
      
      if (onOptionsUpdate) {
        onOptionsUpdate();
      }
    } catch (err) {
      setError('Failed to create service option');
      console.error('Error creating service option:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOption = async (option: TenantServiceOption) => {
    try {
      setLoading(true);
      setError(null);
      
      await upsertTenantServiceOption(tenantId, serviceId, option.key, {
        key: option.key,
        value: option.value || '',
        valueType: option.valueType,
        isRequired: option.isRequired,
        isActive: option.isActive,
        description: option.description || '',
        category: option.category || 'general',
        displayOrder: option.displayOrder || 0
      });
      
      await fetchOptions();
      setEditingOption(null);
      
      if (onOptionsUpdate) {
        onOptionsUpdate();
      }
    } catch (err) {
      setError('Failed to update service option');
      console.error('Error updating service option:', err);
    } finally {
      setLoading(false);
    }
  };

//   const handleDeleteOption = async (optionKey: string) => {
//     if (!confirm('Are you sure you want to delete this service option?')) {
//       return;
//     }

//     try {
//       setLoading(true);
//       setError(null);
//       await deleteTenantServiceOption(tenantId, serviceId, optionKey);
//       await fetchOptions();
      
//       if (onOptionsUpdate) {
//         onOptionsUpdate();
//       }
//     } catch (err) {
//       setError('Failed to delete service option');
//       console.error('Error deleting service option:', err);
//     } finally {
//       setLoading(false);
//     }
//   };

  const resetNewOption = () => {
    setNewOption({
      key: '',
      value: '',
      valueType: 'STRING',
      isRequired: false,
      isActive: true,
      description: '',
      category: 'general',
      displayOrder: 0
    });
  };

  const handleOptionChange = (optionId: string, field: keyof TenantServiceOption, value: any) => {
    setOptions(prev => prev.map(opt => 
      opt.id === optionId ? { ...opt, [field]: value } : opt
    ));
  };

  if (!isOpen) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title=" Service Configuration"
    >
      <div  >
        {/* Enhanced Header */}
        {/* <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FiSettings className="text-indigo-600" />
                Service Configuration
              </h2>
              <p className="text-sm text-gray-600 mt-1">
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white px-3 py-1 rounded-full border border-gray-200">
                <span className="text-xs font-medium text-gray-600">
                  {options.length} Options
                </span>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>
          </div>
        </div> */}

        {/* Enhanced Content */}
        <div  >
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-start gap-3">
              <HiOutlineInformationCircle className="text-red-500 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-medium">Configuration Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Enhanced Add Option Section */}
          {/* <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option Management</h3>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02]"
              >
                <AiOutlinePlus size={16} />
                Add Option
                <HiOutlineSparkles size={14} className="opacity-75" />
              </button>
            </div>
          </div> */}

          {/* Enhanced Add Option Form */}
          {showAddForm && (
            <div className="mb-8 bg-gradient-to-br from-gray-50 to-indigo-50/30 rounded-2xl border border-gray-200 shadow-sm">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <HiOutlineSparkles className="text-indigo-600" size={16} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Create New Option</h3>
                </div>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Option Key *
                      </label>
                      <input
                        type="text"
                        value={newOption.key}
                        onChange={(e) => setNewOption({ ...newOption, key: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        placeholder="e.g., api_endpoint, max_users"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Value
                      </label>
                      <input
                        type="text"
                        value={newOption.value || ''}
                        onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        placeholder="Option value"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Value Type
                      </label>
                      <select
                        value={newOption.valueType}
                        onChange={(e) => setNewOption({ ...newOption, valueType: e.target.value as any })}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      >
                        <option value="STRING">String</option>
                        <option value="NUMBER">Number</option>
                        <option value="BOOLEAN">Boolean</option>
                        <option value="DATE">Date</option>
                        <option value="JSON">JSON</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-700">
                        Category
                      </label>
                      <input
                        type="text"
                        value={newOption.category || ''}
                        onChange={(e) => setNewOption({ ...newOption, category: e.target.value })}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        placeholder="e.g., general, config, security"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={newOption.description || ''}
                      onChange={(e) => setNewOption({ ...newOption, description: e.target.value })}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                      rows={3}
                      placeholder="Describe what this option controls..."
                    />
                  </div>

                  <div className="flex items-center gap-8">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newOption.isRequired || false}
                        onChange={(e) => setNewOption({ ...newOption, isRequired: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Required Option</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newOption.isActive !== false}
                        onChange={(e) => setNewOption({ ...newOption, isActive: e.target.checked })}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        resetNewOption();
                      }}
                      className="px-6 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateOption}
                      disabled={loading || !newOption.key.trim()}
                      className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] shadow-sm hover:shadow-md"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Creating...
                        </span>
                      ) : (
                        "Create Option"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Options List */}
          {loading && !showAddForm ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading options...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {options.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No service options found. Create your first option above.
                </div>
              ) : (
                options.map((option) => (
                  <div key={option.id} className="border border-gray-200 rounded-lg p-4">
                    {editingOption === option.id ? (
                      // Edit mode
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Key</label>
                          <input
                            type="text"
                            value={option.key}
                            onChange={(e) => handleOptionChange(option.id, 'key', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Value</label>
                          <input
                            type="text"
                            value={option.value || ''}
                            onChange={(e) => handleOptionChange(option.id, 'value', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={option.valueType}
                            onChange={(e) => handleOptionChange(option.id, 'valueType', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="STRING">String</option>
                            <option value="NUMBER">Number</option>
                            <option value="BOOLEAN">Boolean</option>
                            <option value="DATE">Date</option>
                            <option value="JSON">JSON</option>
                          </select>
                        </div>
                        <div className="md:col-span-2 lg:col-span-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={option.description || ''}
                            onChange={(e) => handleOptionChange(option.id, 'description', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            rows={2}
                          />
                        </div>
                        <div className="flex items-center gap-4 md:col-span-2 lg:col-span-3">
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={option.isRequired || false}
                              onChange={(e) => handleOptionChange(option.id, 'isRequired', e.target.checked)}
                              className="mr-1"
                            />
                            Required
                          </label>
                          <label className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              checked={option.isActive !== false}
                              onChange={(e) => handleOptionChange(option.id, 'isActive', e.target.checked)}
                              className="mr-1"
                            />
                            Active
                          </label>
                          <div className="flex gap-2 ml-auto">
                            <button
                              onClick={() => handleUpdateOption(option)}
                              className="p-1 text-green-600 hover:text-green-800 transition-colors"
                              title="Save changes"
                            >
                              <AiOutlineSave size={16} />
                            </button>
                            <button
                              onClick={() => setEditingOption(null)}
                              className="p-1 text-gray-600 hover:text-gray-800 transition-colors"
                              title="Cancel edit"
                            >
                              <AiOutlineClose size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{option.key}</div>
                              <div className="text-xs text-gray-500">Option Key</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-700">{option.value || 'No value'}</div>
                              <div className="text-xs text-gray-500">Value</div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-700">{option.valueType}</div>
                              <div className="text-xs text-gray-500">Type</div>
                            </div>
                          </div>
                          
                          {option.description && (
                            <div className="mb-3">
                              <div className="text-sm text-gray-600">{option.description}</div>
                              <div className="text-xs text-gray-500">Description</div>
                            </div>
                          )}

                          {/* Toggle Controls */}
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700">Active:</span>
                              <button
                                onClick={() => {
                                  const updatedOption = { ...option, isActive: !option.isActive };
                                  handleUpdateOption(updatedOption);
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                  option.isActive ? 'bg-green-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    option.isActive ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              <span className={`text-xs px-2 py-1 rounded ${
                                option.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {option.isActive ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-700">Required:</span>
                              <button
                                onClick={() => {
                                  const updatedOption = { ...option, isRequired: !option.isRequired };
                                  handleUpdateOption(updatedOption);
                                }}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                  option.isRequired ? 'bg-red-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    option.isRequired ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                              <span className={`text-xs px-2 py-1 rounded ${
                                option.isRequired ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {option.isRequired ? 'Required' : 'Optional'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => setEditingOption(option.id)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit option details"
                          >
                            <AiOutlineEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteOption(option.key)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete option"
                          >
                            <AiOutlineDelete size={16} />
                          </button>
                        </div> */}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Dialog>

  );
};

export default TenantServiceOptionsModal;